/**
 * Tachograph utility functions for activity timeline processing,
 * duration formatting, and fleet RAG status computation.
 */

/* ---- Constants ---- */

export const MINUTES_PER_DAY = 1440;

/* The raw hues. Nothing reads this table any more: the Interrogator page
   (TachoInterrogator.jsx, deleted 2026-09-09) was its one reader, and it
   concatenated an alpha onto these for a wash and a hairline, which is why
   they stayed hex. The table stays as the dark value each TYPE_INK token
   below pins to. Nothing READABLE may read this table; read TYPE_INK. */
export const TYPE_COLORS = {
  driving: '#e8594e',
  work: '#e5a33b',
  availability: '#7c9cc9',
  rest: '#3cb371',
  unknown: '#64748b',
};

/* The same five, as tokens, for every position a user actually reads: the KPI
   figures on the Speeding tab, the timeline blocks, the legend swatches, the
   hover label. Each token's dark value is the hex above it, so dark and high
   contrast do not move; light gets an ink that survives a white card, where
   #e5a33b measures 2.15:1 and #3cb371 1.94:1. */
export const TYPE_INK = {
  driving: 'var(--ink-danger)',
  work: 'var(--ink-amber)',
  availability: 'var(--ink-blue)',
  rest: 'var(--ink-green)',
  unknown: 'var(--ink-neutral)',
};

export const TYPE_LABELS = {
  driving: 'Driving',
  work: 'Work',
  availability: 'Availability',
  rest: 'Rest',
  unknown: 'Unknown',
};

/* ---- Functions ---- */

/**
 * Convert raw activity type strings to a standard lowercase type.
 *
 * Handles common variations such as:
 *   'DRIVING', 'Drive', '1' → 'driving'
 *   'OTHER WORK', 'Work', '2' → 'work'
 *   'AVAILABILITY', 'Available', '3' → 'availability'
 *   'REST', 'Break', '0' → 'rest'
 *
 * Returns 'unknown' for unrecognised values.
 *
 * @param {string|number} raw
 * @returns {'driving'|'work'|'availability'|'rest'|'unknown'}
 */
export function normalizeType(raw) {
  if (raw == null) return 'unknown';

  const val = String(raw).trim().toLowerCase();

  // Numeric tachograph codes
  if (val === '0' || val === 'break' || val === 'rest' || val === 'daily rest' || val === 'weekly rest') {
    return 'rest';
  }
  if (val === '1' || val === 'driving' || val === 'drive') {
    return 'driving';
  }
  if (val === '2' || val === 'work' || val === 'other work' || val === 'other_work') {
    return 'work';
  }
  if (val === '3' || val === 'availability' || val === 'available') {
    return 'availability';
  }

  // Broader pattern matches
  if (/driv/i.test(val)) return 'driving';
  if (/work/i.test(val)) return 'work';
  if (/avail/i.test(val)) return 'availability';
  if (/rest|break|sleep/i.test(val)) return 'rest';

  return 'unknown';
}

/**
 * Convert a "HH:MM" string or numeric minute count to minute-of-day (0-1439).
 *
 * @param {string|number} timeStr
 * @returns {number}
 */
export function parseMinute(timeStr) {
  if (typeof timeStr === 'number') {
    return Math.max(0, Math.min(MINUTES_PER_DAY - 1, Math.floor(timeStr)));
  }

  const str = String(timeStr).trim();

  // Try HH:MM or HH:MM:SS format
  const match = str.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (match) {
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    return Math.max(0, Math.min(MINUTES_PER_DAY - 1, hours * 60 + minutes));
  }

  // Fall back to numeric parse
  const num = parseInt(str, 10);
  if (!isNaN(num)) {
    return Math.max(0, Math.min(MINUTES_PER_DAY - 1, num));
  }

  return 0;
}

/**
 * Convert a minute-of-day (0-1439) to "HH:MM" format.
 *
 * @param {number} minute
 * @returns {string}
 */
export function formatMinute(minute) {
  const clamped = Math.max(0, Math.min(MINUTES_PER_DAY - 1, Math.floor(minute)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Create a 1440-element array from activity records where each index
 * represents one minute of the day and contains the activity type for
 * that minute.
 *
 * Each activity object should have:
 *   - DateTimeStart (or start): a time string or minute number
 *   - DurationMinutes (or duration): duration in minutes
 *   - ActivityType (or type): the raw activity type
 *
 * Minutes not covered by any activity are filled with 'rest'.
 *
 * @param {Array<Object>} activities
 * @returns {string[]} array of length 1440
 */
export function mapActivitiesToMinutes(activities) {
  const minutes = new Array(MINUTES_PER_DAY).fill('rest');

  if (!Array.isArray(activities)) return minutes;

  for (const act of activities) {
    // Resolve start minute
    const rawStart = act.DateTimeStart ?? act.start ?? act.Start ?? null;
    if (rawStart == null) continue;

    let startMinute;
    if (typeof rawStart === 'string' && (rawStart.includes('T') || rawStart.includes(' '))) {
      // Datetime string – extract HH:MM portion (handles both "T" and space separator)
      const timePart = rawStart.includes('T') ? rawStart.split('T')[1] : rawStart.split(' ')[1];
      startMinute = parseMinute(timePart);
    } else {
      startMinute = parseMinute(rawStart);
    }

    // Resolve duration
    const duration = Math.floor(
      Number(act.DurationMinutes ?? act.duration ?? act.Duration ?? 0),
    );

    // Resolve type
    const type = normalizeType(
      act.ActivityType ?? act.type ?? act.Type ?? 'unknown',
    );

    // Fill the minute array
    const end = Math.min(startMinute + duration, MINUTES_PER_DAY);
    for (let i = startMinute; i < end; i++) {
      minutes[i] = type;
    }
  }

  return minutes;
}

/**
 * Compute a summary from a 1440-element minute array.
 *
 * Returns hours spent in each activity type.
 *
 * @param {string[]} minuteArray – length 1440
 * @returns {{ driving: number, work: number, availability: number, rest: number, total: number }}
 */
export function computeDaySummary(minuteArray) {
  const counts = { driving: 0, work: 0, availability: 0, rest: 0 };

  if (!Array.isArray(minuteArray)) {
    return { ...counts, total: 0 };
  }

  for (const type of minuteArray) {
    const normalised = normalizeType(type);
    if (normalised in counts) {
      counts[normalised]++;
    }
  }

  const total = counts.driving + counts.work + counts.availability + counts.rest;

  return {
    driving: counts.driving / 60,
    work: counts.work / 60,
    availability: counts.availability / 60,
    rest: counts.rest / 60,
    total: total / 60,
  };
}

/**
 * Where to place the WTD 6-hour ("worked over 6 hours without a break") pill on
 * the day timeline: the minute at which accumulated WORKING time first reaches
 * 6 hours (360 min) with no qualifying break since duty start / the last break.
 *
 * Working time = driving + other work. Availability (POA) is NOT working time
 * and is NOT a break, so it neither adds to nor resets the counter. A rest of
 * >= 15 min is a qualifying break and resets the counter. Mirrors the engine's
 * rule and the existing EU-561 4.5h break-pill positioning.
 *
 * Segment minutes are assumed already localised (Europe/London) by the caller,
 * so this is timezone-agnostic. Returns the minute-of-day, or null when 6h of
 * working time is never reached (or the input is empty).
 *
 * @param {Array<{type:string,startMinute:number,endMinute:number,duration:number}>} segments
 * @returns {number|null}
 */
export function wtdSixHourMinute(segments) {
  if (!Array.isArray(segments) || segments.length === 0) return null;
  const LIMIT = 360; // 6h of working time
  let cumWork = 0;
  for (const seg of segments) {
    if (seg.type === 'driving' || seg.type === 'work') {
      const dur = Number(seg.duration) || 0;
      cumWork += dur;
      if (cumWork >= LIMIT) {
        // Back off within this segment to the exact 6h crossing point.
        return seg.startMinute + Math.max(0, dur - (cumWork - LIMIT));
      }
    } else if (seg.type === 'rest' && (Number(seg.duration) || 0) >= 15) {
      cumWork = 0;
    }
    // availability / unknown / short rest: no add, no reset.
  }
  return null;
}

/* ──────────────────────────────────────────────────────────────
   RAG styles (ported from the legacy IDHA dashboard, adapted to
   TachoParser data shapes — DASH1).
   ────────────────────────────────────────────────────────────── */

/**
 * RAG style map for the banners and health drills. The legacy IDHA
 * app used Tailwind utility classes; the TachoParser app styles with
 * inline-style objects + CSS variables, so this returns ready-to-spread
 * style objects rather than className strings.
 */
export const RAG_STATUS_STYLES = {
  green: {
    background: 'rgba(var(--success-rgb), 0.12)',
    border: '1px solid rgba(var(--success-rgb), 0.45)',
    color: 'var(--ink-green-soft)',
  },
  amber: {
    background: 'rgba(var(--warning-rgb), 0.12)',
    border: '1px solid rgba(var(--warning-rgb), 0.45)',
    color: 'var(--ink-amber-soft)',
  },
  red: {
    background: 'rgba(var(--danger-rgb), 0.12)',
    border: '1px solid rgba(var(--danger-rgb), 0.45)',
    color: 'var(--ink-danger-soft)',
  },
};

/**
 * Format decimal hours as a human-readable "Xh Ym" string.
 *
 * @param {number} hours – decimal hours (e.g. 2.75)
 * @returns {string} e.g. "2h 45m"
 */
export function formatDuration(hours) {
  if (hours == null || isNaN(hours)) return '0h 0m';

  const totalMinutes = Math.round(Math.abs(hours) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  return `${h}h ${m}m`;
}

/**
 * Returns the ISO 8601 week number for a given date.
 *
 * @param {Date|string} date
 * @returns {number}
 */
export function getWeekNumber(date) {
  const d = new Date(date);
  // Set to nearest Thursday: current date + 4 - current day number (Mon=1, Sun=7)
  const dayOfWeek = d.getDay() || 7; // convert Sunday from 0 to 7
  d.setDate(d.getDate() + 4 - dayOfWeek);

  // Get January 1st of that year
  const yearStart = new Date(d.getFullYear(), 0, 1);

  // Calculate full weeks between yearStart and nearest Thursday
  const weekNumber = Math.ceil(
    ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );

  return weekNumber;
}

/* ──────────────────────────────────────────────────────────────
   UK local-time display (#191)

   Tachograph data is recorded — and stored by us — in UTC, often as a
   naive "YYYY-MM-DDTHH:MM:SS" string with no zone marker. For display we
   render UK local time (GMT in winter, BST in summer) to match how
   Tachomaster / Convey and UK operators read the day. Conversion is
   DST-correct via the IANA tz database (Europe/London) and is purely a
   presentation concern — stored/engine data stays UTC.
   ────────────────────────────────────────────────────────────── */

/**
 * Parse a tachograph timestamp as a UTC *instant*.
 *
 * Naive strings (no 'Z' / no ±HH:MM offset) are treated as UTC — NOT as the
 * viewer's machine-local time, which `new Date('2026-05-20T05:08:00')` would
 * wrongly assume. Accepts 'T' or space separators, with or without seconds.
 *
 * @param {string|Date} s
 * @returns {Date|null} the instant, or null if unparseable
 */
export function parseUtcInstant(s) {
  if (s == null) return null;
  if (s instanceof Date) return isNaN(s.getTime()) ? null : s;
  const str = String(s).trim();
  if (!str) return null;
  const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(str);
  let iso = str.replace(' ', 'T');
  if (!hasTz) iso += 'Z';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

let _londonFmt = null;
function londonFormatter() {
  if (_londonFmt) return _londonFmt;
  _londonFmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  return _londonFmt;
}

/**
 * Convert a UTC tacho timestamp to its Europe/London wall-clock parts.
 *
 * @param {string|Date} s
 * @returns {{date:string, time:string, dateTime:string, minute:number}|null}
 *   date = 'YYYY-MM-DD', time = 'HH:MM', dateTime = 'YYYY-MM-DDTHH:MM:SS'
 *   (all in London local time), minute = minute-of-day 0..1439
 */
export function utcToLondonParts(s) {
  const d = parseUtcInstant(s);
  if (!d) return null;
  const parts = {};
  for (const p of londonFormatter().formatToParts(d)) parts[p.type] = p.value;
  // Some ICU builds emit '24' for midnight under hour12:false — normalise to '00'.
  const hour = parts.hour === '24' ? '00' : parts.hour;
  const date = `${parts.year}-${parts.month}-${parts.day}`;
  const time = `${hour}:${parts.minute}`;
  return {
    date,
    time,
    dateTime: `${date}T${time}:${parts.second}`,
    minute: Number(hour) * 60 + Number(parts.minute),
  };
}

/**
 * UTC tacho timestamp → Europe/London wall-clock ISO-ish string
 * "YYYY-MM-DDTHH:MM:SS" (no zone marker). Feeding this back through the
 * existing HH:MM-slicing / minute-of-day machinery yields UK local display.
 * Returns the input unchanged if unparseable (defensive).
 *
 * @param {string|Date} s
 * @returns {string|null}
 */
export function utcToLondonWallClock(s) {
  const p = utcToLondonParts(s);
  if (p) return p.dateTime;
  return s == null ? s : String(s);
}

/**
 * Shift every "HH:MM" occurrence in a free-text string (e.g. an infringement's
 * Violation prose "…exceeded at 11:26, between 05:35 and 11:26…") from stored
 * UTC to Europe/London wall-clock, so the description reads the same local time
 * as the row/pill it accompanies. The times are anchored to `isoDate` for the
 * DST-correct offset (BST +1h in summer, GMT +0h in winter). Only the matched
 * HH:MM digits change; everything else is untouched. Returns the input
 * unchanged when the date is missing/unparseable or the input isn't a string.
 *
 * @param {string} text
 * @param {string} isoDate  'YYYY-MM-DD' or an ISO datetime (date part is used)
 * @returns {string}
 */
export function localizeHhmmInText(text, isoDate) {
  if (typeof text !== 'string' || !text) return text;
  const date = String(isoDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return text;
  return text.replace(/\b(\d{1,2}):(\d{2})\b/g, (m, hh, mm) => {
    const h = Number(hh);
    const mn = Number(mm);
    if (h > 23 || mn > 59) return m; // not a clock time (e.g. a ratio) — leave it
    const p = utcToLondonParts(`${date}T${String(h).padStart(2, '0')}:${mm}:00`);
    return p ? p.time : m;
  });
}

/**
 * UTC tacho timestamp → Europe/London "HH:MM" clock label.
 * Returns '--:--' for empty/unparseable input.
 *
 * @param {string|Date} s
 * @returns {string}
 */
export function londonClock(s) {
  const p = utcToLondonParts(s);
  return p ? p.time : '--:--';
}

/**
 * Build the download / print filename for an infringement letter or closure
 * document: `<surname>-dd-mm-yy-infringement`.
 *
 * The surname is the driver name's LAST whitespace-delimited token; the date
 * segment is derived from the infringement Date (dd-mm-yy). Browsers use a
 * printed document's <title> as the default "Save as PDF" filename, so this is
 * fed into the printable HTML's <title> (and any download anchor).
 *
 * Falls back to "driver" for a missing name and omits the date segment when the
 * date is missing/unparseable. Unsafe filename characters are stripped.
 *
 * @param {string} driverName  e.g. "Jane Driver"
 * @param {string} date        ISO date or datetime, e.g. "2026-06-01" / "...T09:30:00"
 * @returns {string}           e.g. "Driver-01-06-26-infringement"
 */
export function infringementDocFilename(driverName, date) {
  const tokens = String(driverName || '').trim().split(/\s+/).filter(Boolean);
  const rawSurname = tokens.length ? tokens[tokens.length - 1] : 'driver';
  // Keep letters, digits and hyphens; drop apostrophes/spaces/other punctuation.
  const surname = rawSurname.replace(/[^A-Za-z0-9-]/g, '') || 'driver';

  const iso = String(date || '').slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const parts = [surname];
  if (m) parts.push(`${m[3]}-${m[2]}-${m[1].slice(2)}`);
  parts.push('infringement');
  return parts.join('-');
}

/**
 * Inject/replace the <title> of a printable HTML document so the browser's
 * "Save as PDF" default filename matches the given title. Browsers derive the
 * Save-as filename from the printed document's <title>; the letter/closure HTML
 * each ship a static title, so we swap it (or insert one into <head>) before
 * handing the HTML to a srcDoc print iframe. No-op for empty inputs.
 *
 * @param {string} html
 * @param {string} title
 * @returns {string}
 */
export function withDocTitle(html, title) {
  if (!html || !title) return html;
  if (/<title>[\s\S]*?<\/title>/i.test(html)) {
    return html.replace(/<title>[\s\S]*?<\/title>/i, `<title>${title}</title>`);
  }
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/(<head[^>]*>)/i, `$1<title>${title}</title>`);
  }
  return html;
}

/**
 * Format a DATA-COVERAGE value — how far a driver's or vehicle's data runs
 * in the .ddd files — as a plain date.
 *
 * NOT a datetime. last_activity_date is date-only ("2026-08-11"), and
 * `new Date("2026-08-11")` parses as UTC MIDNIGHT. Localising that in
 * British Summer Time printed "11 Aug 26 01:00" on the Last Card Reads
 * board — an hour that appears in no tacho file. Every row showed a
 * fabricated 01:00, and the single row carrying a genuine VU timestamp
 * (06:21) read as the anomaly when it was the only honest value there.
 * The same parse rolls the DATE back a day for any viewer behind UTC.
 *
 * So the Y-M-D parts are pulled from the string and a LOCAL date is built
 * from them — no timezone conversion, no invented clock time. A full
 * timestamp is accepted too (its date part is used), because a coverage
 * value is a date whatever precision the source happened to carry.
 *
 * The companion "uploaded ..." line keeps fmtShortDateTime: that one is a
 * real upload instant where the time genuinely means something.
 */
export function fmtCoverageDate(raw) {
  if (!raw) return 'Never';
  const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]),
               Number(m[4] || 0), Number(m[5] || 0))
    : new Date(String(raw));
  if (isNaN(d.getTime())) return 'Never';
  const date = d.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: '2-digit',
  });
  // A time is shown ONLY when the source recorded one. Vehicles on an
  // automatic downloader carry a genuine VU coverage timestamp (YK22HKU
  // reads 06:21 — its driver starts around six), and that is real
  // information worth keeping. Date-only values get no clock, which is
  // what killed the fabricated 01:00.
  if (!m || m[4] === undefined) return date;
  return `${date} ${m[4]}:${m[5]}`;
}

/**
 * Shape one DDIR walkaround check (GET /api/walkarounds) for its Activity Log
 * row. Pure — colours and markup stay in the component; this decides only
 * WHAT the row says, which is the part worth testing.
 *
 * Three states, because "not pass" and "fail" are not the same claim:
 *   pass    -> the driver checked the vehicle and it was fine
 *   fail    -> the check found something
 *   unknown -> a `result` that is neither. The webhook rejects anything but
 *              'pass'/'fail' with a 400, so this should be unreachable — but
 *              defaulting an unrecognised value to PASS would print a
 *              reassurance nobody made, on a compliance record. Unknown says
 *              "a check happened, we can't vouch for its outcome".
 *
 * The defect count is shown whenever it is non-zero — INCLUDING on a pass.
 * An advisory defect logged on an otherwise-passed check is exactly the thing
 * an operator wants to catch early, and hiding it because the header said
 * "pass" would be the paperwork answer, not the prevention answer.
 *
 * `durationLabel` is how long the check took (started_at -> completed_at), or
 * null when it must not be shown. See WALKAROUND_MAX_DURATION_MS below for the
 * whole of that rule — it is the compliance-sensitive part of this helper.
 *
 * @param {{result?: string, defect_count?: number, started_at?: string,
 *          completed_at?: string, started_at_utc?: string,
 *          completed_at_utc?: string,
 *          defects?: Array<{item?: string, severity?: string, note?: string}>}} w
 * @returns {{state: 'pass'|'fail'|'unknown', failed: boolean, label: string,
 *            defectCount: number, defectLabel: (string|null),
 *            defectSummary: (string|null), durationLabel: (string|null)}}
 */
export function walkaroundPill(w) {
  const raw = String(w?.result ?? '').trim().toLowerCase();
  const state = raw === 'pass' ? 'pass' : raw === 'fail' ? 'fail' : 'unknown';

  // Coerce rather than trust: the count arrives as JSON and a bad sender
  // could ship a string. A non-number is "unknown", which means no pill —
  // never a fabricated "0 DEFECTS" or a NaN on screen.
  const n = Number(w?.defect_count);
  const defectCount = Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;

  // Singular/plural: "1 DEFECT", "2 DEFECTS" — the row is read at a glance
  // and "1 DEFECTS" is the kind of wrongness that makes an operator distrust
  // the rest of the screen.
  const defectLabel = defectCount > 0
    ? `${defectCount} ${defectCount === 1 ? 'DEFECT' : 'DEFECTS'}`
    : null;

  // Hover detail: "Nearside mirror (major) — cracked". `defects` is optional
  // on the wire (contract B2), so a check can legitimately report a count
  // with no itemisation; entries missing an item name are dropped rather
  // than rendered as an empty bullet.
  const items = Array.isArray(w?.defects) ? w.defects : [];
  const parts = items.map((d) => {
    const item = String(d?.item ?? '').trim();
    if (!item) return '';
    const severity = String(d?.severity ?? '').trim();
    const note = String(d?.note ?? '').trim();
    return `${item}${severity ? ` (${severity})` : ''}${note ? ` — ${note}` : ''}`;
  }).filter(Boolean);

  return {
    state,
    failed: state === 'fail',
    label: state === 'fail' ? 'WALKAROUND FAIL'
      : state === 'pass' ? 'WALKAROUND PASS'
      : 'WALKAROUND',
    defectCount,
    defectLabel,
    defectSummary: parts.length ? parts.join('; ') : null,
    durationLabel: walkaroundDurationLabel(w),
  };
}

/**
 * The longest elapsed walkaround time that may be displayed: 4 hours.
 *
 * This number is NOT arbitrary and must not be re-picked. It is the same
 * threshold DDIR's own `walkaroundTimeline.js` applies, deliberately, so that
 * one check cannot look different in the two products.
 */
export const WALKAROUND_MAX_DURATION_MS = 4 * 60 * 60 * 1000;

/**
 * How long a walkaround took, formatted for the Activity Log's Duration
 * column — or null when it must not be shown.
 *
 * WHY a duration at all: a driver who "completed" a full vehicle check in 25
 * seconds did not perform one, and the elapsed time is the only evidence of
 * that. It is the point of the field.
 *
 * WHY the ceiling: the signal is ASYMMETRIC. A very short span is strong
 * evidence — the form cannot have been open longer than it was open. A LONG
 * span proves nothing at all, because DDIR's check screen lets a draft be
 * backgrounded and resumed up to 12h later, so a 5-hour span may be a
 * thorough inspection or an abandoned draft and the timestamps cannot tell
 * them apart. Rather than print a number that invites the wrong inference,
 * the row collapses back to a bare instant above 4h.
 *
 * WHAT IT IS NOT: elapsed time, and nothing else. No fast-check flag, no
 * score, no tier — that is a separate decision needing DDIR and a TM. And
 * nothing keyed to length may reach the styling either: a long duration must
 * never read as diligence, because an operator will infer that if the UI
 * implies it. Hence a plain string, rendered in the same cell, colour and
 * weight as every other duration on the table.
 *
 * null (no duration, row otherwise unchanged) when:
 *   * started_at is absent — the COMMON case, not an error;
 *   * either timestamp is unparseable;
 *   * the pair is INVERTED (started_at after completed_at) — a bad handset
 *     clock. The check is still stored and still rendered; only the duration
 *     is withheld. We never reject one, because a rejection makes DDIR stop
 *     retrying and loses a real compliance record;
 *   * the span exceeds 4h.
 *
 * @param {object} w a walkaround row from GET /api/walkarounds
 * @returns {string|null} e.g. '0h 26m'
 */
function walkaroundDurationLabel(w) {
  // The RAW UTC pair wins when the caller preserved it. Elapsed time is a
  // real-world quantity and a wall-clock delta is not it across a DST change
  // — the day view localises timestamps for display, so it keeps the
  // originals for this. Falling back to the display copies keeps the helper
  // usable on an un-localised row (and both are on the same scale either way).
  const start = parseUtcInstant(w?.started_at_utc ?? w?.started_at);
  const end = parseUtcInstant(w?.completed_at_utc ?? w?.completed_at);
  if (!start || !end) return null;

  const elapsedMs = end.getTime() - start.getTime();
  // Both bounds tested on the RAW milliseconds, not on the rounded minutes:
  // a 4h00m30s span would otherwise round back under the line and print.
  if (elapsedMs < 0 || elapsedMs > WALKAROUND_MAX_DURATION_MS) return null;

  // Mirrors DeepDive's fmtHM ("0h 26m", "13h 18m") so the walkaround row is
  // indistinguishable from the driving and rest rows around it. Rounded to
  // whole minutes FIRST, then split, so 59.7 minutes reads '1h 0m' and never
  // '0h 60m' — a walkaround carries real seconds where an activity segment
  // does not.
  const totalMinutes = Math.round(elapsedMs / 60000);
  return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
}
